import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid, Card, MenuItem,
  CircularProgress, Stack, IconButton, Switch, FormControlLabel, Chip,
} from "@mui/material";
import { ArrowBack, AddPhotoAlternate, Delete } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  getDailyTaskByIdAction,
  clearGetDailyTaskByIdAction,
  updateDailyTaskAction,
  clearUpdateDailyTaskAction,
  getAllUserAction,
} from "../../store/actions";

const FIELD_STYLE = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
  fontFamily: "DM Sans, sans-serif",
};

const STATUS_OPTIONS = [
  { value: "ASSIGNED",  label: "Assigned" },
  { value: "COMPLETED", label: "Completed" },
  { value: "APPROVED",  label: "Approved" },
  { value: "REJECTED",  label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const EMPTY_FORM = {
  task_title: "",
  task_description: "",
  task_date: "",
  assigned_to_user_id: "",
  assigned_by_user_id: "",
  status: "ASSIGNED",
  is_late: false,
  is_cancelled: false,
  completion_time: "",
  completion_remarks: "",
  action_remarks: "",
  action_by_user_id: "",
};

export default function EditTask() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  let { taskId } = useParams();
  // Support navigation via state (no param)
  if (!taskId && location.state && location.state.taskId) {
    taskId = location.state.taskId;
  }

  const userDetails = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("userDetails")); }
    catch { return null; }
  }, []);

  const currentUserId = userDetails?.id || "";
  const basePath = location.pathname.startsWith("/masterData")
    ? "/masterData"
    : location.pathname.startsWith("/amrm")
    ? "/amrm"
    : "/SOorMDO";

  // Selectors
  const { loading: fetchLoading, successData: taskData } = useSelector(
    (state) => state.getDailyTaskByIdReducer || {}
  );
  const {
    loading: updateLoading,
    successData: updateSuccess,
    error: updateError,
    errorInfo: updateErrorInfo,
  } = useSelector((state) => state.updateDailyTaskReducer || {});

  const { successData: usersData } = useSelector((state) => state.getAllUserReducer || {});

  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const [form, setForm]               = useState(EMPTY_FORM);
  const [formPopulated, setFormPopulated] = useState(false);
  const [images, setImages]           = useState([]);
  const [imgPreviews, setImgPreviews] = useState([]);
  const fileInputRef                  = useRef(null);

  // Assigned user can only edit: status, action_by_user_id, action_remarks, is_late, is_cancelled
  // Assigner/admin can edit all fields
  const isAssignedUser = !!currentUserId && form.assigned_to_user_id === currentUserId;

  // Fetch on mount
  useEffect(() => {
    dispatch(getAllUserAction());
    dispatch(clearGetDailyTaskByIdAction());
    dispatch(clearUpdateDailyTaskAction());
    if (taskId) {
      dispatch(getDailyTaskByIdAction({ endPoint: `daily-task/${taskId}` }));
    }
    return () => {
      dispatch(clearGetDailyTaskByIdAction());
      dispatch(clearUpdateDailyTaskAction());
    };
  }, [dispatch, taskId]);

  // Pre-fill form when task data arrives
  useEffect(() => {
    if (taskData && !formPopulated) {
      const t =
        taskData?.data?.dailyTask ||
        taskData?.data?.task ||
        taskData?.data ||
        taskData;

      const VALID_STATUSES = STATUS_OPTIONS.map((s) => s.value);
      const apiStatus = t.status || "";
      const safeStatus = VALID_STATUSES.includes(apiStatus) ? apiStatus : "ASSIGNED";

      setForm({
        task_title:             t.taskTitle            || "",
        task_description:       t.taskDescription      || "",
        task_date:              t.taskDate ? t.taskDate.slice(0, 10) : "",
        assigned_to_user_id:    t.assignedToUserId      || "",
        assigned_by_user_id:    t.assignedByUserId      || "",
        status:                 safeStatus,
        is_late:                t.isLate               || false,
        is_cancelled:           t.isCancelled          || false,
        completion_time:        t.completionTime
          ? t.completionTime.slice(0, 16)
          : "",
        completion_remarks:     t.completionRemarks    || "",
        action_remarks:         t.actionRemarks        || "",
        action_by_user_id:      t.actionByUserId       || "",
      });
      setFormPopulated(true);
    }
  }, [taskData, formPopulated]);

  // Success / error side-effects
  useEffect(() => {
    if (updateSuccess) {
      toast.success("Task updated successfully!");
      dispatch(clearUpdateDailyTaskAction());
      navigate(`${basePath}/dailyTask`);
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateError) {
      toast.error(
        updateErrorInfo?.message || updateErrorInfo || "Failed to update task."
      );
      dispatch(clearUpdateDailyTaskAction());
    }
  }, [updateError]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  function handleAddImages(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setImages((prev) => [...prev, ...files]);
    setImgPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  }

  function handleRemoveImage(i) {
    URL.revokeObjectURL(imgPreviews[i]);
    setImages((prev) => prev.filter((_, idx) => idx !== i));
    setImgPreviews((prev) => prev.filter((_, idx) => idx !== i));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const fd = new FormData();
    if (form.task_title.trim())         fd.append("task_title",          form.task_title.trim());
    if (form.task_description.trim())   fd.append("task_description",    form.task_description.trim());
    if (form.task_date)                 fd.append("task_date",           form.task_date);
    if (form.assigned_to_user_id)       fd.append("assigned_to_user_id", form.assigned_to_user_id);
    if (form.assigned_by_user_id)       fd.append("assigned_by_user_id", form.assigned_by_user_id);
    if (form.status)                    fd.append("status",              form.status);
    if (form.is_late)                   fd.append("is_late",      "true");
    if (form.is_cancelled)              fd.append("is_cancelled", "true");
    
    // Auto-set completion_time to current datetime for assigned users
    if (isAssignedUser) {
      const now = new Date();
      const completionTime = now.toISOString().slice(0, 16);
      fd.append("completion_time", completionTime);
    } else if (form.completion_time) {
      fd.append("completion_time", form.completion_time);
    }
    
    if (form.completion_remarks.trim()) fd.append("completion_remarks",  form.completion_remarks.trim());
    if (form.action_remarks.trim())     fd.append("action_remarks",      form.action_remarks.trim());
    // Auto-set action_by_user_id to current logged-in user for higher employees
    if (!isAssignedUser && currentUserId) {
      fd.append("action_by_user_id", currentUserId);
    } else if (form.action_by_user_id) {
      fd.append("action_by_user_id", form.action_by_user_id);
    }
    images.forEach((img) => fd.append("completion_photo_paths", img));
    if (currentUserId) fd.append("user_id", currentUserId);
    fd.endPoint = `daily-task/${taskId}`;
    dispatch(updateDailyTaskAction(fd));
  }

  if (fetchLoading && !formPopulated) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress sx={{ color: "#4318FF" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1B2559", fontFamily: "DM Sans, sans-serif" }}>
            Edit Task
          </Typography>
          <Typography variant="body2" sx={{ color: "#A3AED0", fontFamily: "DM Sans, sans-serif" }}>
            Update the task details below
          </Typography>
        </Box>
      </Stack>

      <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid #E8EDF5", p: { xs: 2, md: 4 } }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>


          <Grid container spacing={2.5}>

            {/* Task Title - Visible to all, read-only */}
            <Grid item size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Task Title"
                name="task_title"
                value={form.task_title}
                onChange={handleChange}
                fullWidth
                placeholder="Enter task title…"
                disabled
                sx={FIELD_STYLE}
              />
            </Grid>

            {/* Assigned To - Visible to all, read-only */}
            <Grid item size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Assigned To"
                name="assigned_to_user_id"
                value={form.assigned_to_user_id}
                fullWidth
                disabled
                sx={FIELD_STYLE}
              >
                {users.length === 0 && (
                  <MenuItem disabled value="">No users available</MenuItem>
                )}
                {users.map((u) => (
                  <MenuItem key={u.userId} value={u.userId}>
                    {u.name || u.userId}{u.role ? ` (${u.role})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Task Description - Editable by assigner, read-only for others */}
            <Grid item size={{ xs: 12 }}>
              <TextField
                label="Description"
                name="task_description"
                value={form.task_description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Describe the task in detail…"
                disabled={!isAssignedUser}
                sx={FIELD_STYLE}
              />
            </Grid>

            {/* Status Toggle Buttons - Different options based on user role */}
            <Grid item size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#1B2559", fontFamily: "DM Sans, sans-serif", fontWeight: 600 }}>
                Task Status
              </Typography>
              <Stack direction="row" spacing={{ xs: 0.75, sm: 1.5 }} sx={{ width: "100%" }}>
                {isAssignedUser ? (
                  <>
                    <Button
                      variant={form.status === "COMPLETED" ? "contained" : "outlined"}
                      onClick={() => setForm((prev) => ({ ...prev, status: "COMPLETED" }))}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        flex: 1,
                        minWidth: { xs: 0, sm: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        px: { xs: 1, sm: 2 },
                        borderColor: form.status === "COMPLETED" ? "#059669" : "#E5E7EB",
                        bgcolor: form.status === "COMPLETED" ? "#059669" : "transparent",
                        color: form.status === "COMPLETED" ? "#fff" : "#64748B",
                        "&:hover": {
                          borderColor: "#059669",
                          bgcolor: form.status === "COMPLETED" ? "#047857" : "rgba(5, 150, 105, 0.08)",
                        },
                      }}
                    >
                      Completed
                    </Button>
                    <Button
                      variant={form.status === "REJECTED" ? "contained" : "outlined"}
                      onClick={() => setForm((prev) => ({ ...prev, status: "REJECTED" }))}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        flex: 1,
                        minWidth: { xs: 0, sm: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        px: { xs: 1, sm: 2 },
                        borderColor: form.status === "REJECTED" ? "#DC2626" : "#E5E7EB",
                        bgcolor: form.status === "REJECTED" ? "#DC2626" : "transparent",
                        color: form.status === "REJECTED" ? "#fff" : "#64748B",
                        "&:hover": {
                          borderColor: "#DC2626",
                          bgcolor: form.status === "REJECTED" ? "#B91C1C" : "rgba(220, 38, 38, 0.08)",
                        },
                      }}
                    >
                      Rejected
                    </Button>
                    <Button
                      variant={form.status === "CANCELLED" ? "contained" : "outlined"}
                      onClick={() => setForm((prev) => ({ ...prev, status: "CANCELLED" }))}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        flex: 1,
                        minWidth: { xs: 0, sm: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        px: { xs: 1, sm: 2 },
                        borderColor: form.status === "CANCELLED" ? "#6B7280" : "#E5E7EB",
                        bgcolor: form.status === "CANCELLED" ? "#6B7280" : "transparent",
                        color: form.status === "CANCELLED" ? "#fff" : "#64748B",
                        "&:hover": {
                          borderColor: "#6B7280",
                          bgcolor: form.status === "CANCELLED" ? "#4B5563" : "rgba(107, 114, 128, 0.08)",
                        },
                      }}
                    >
                      Cancelled
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant={form.status === "APPROVED" ? "contained" : "outlined"}
                      onClick={() => setForm((prev) => ({ ...prev, status: "APPROVED" }))}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        flex: 1,
                        minWidth: { xs: 0, sm: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        px: { xs: 1, sm: 2 },
                        borderColor: form.status === "APPROVED" ? "#059669" : "#E5E7EB",
                        bgcolor: form.status === "APPROVED" ? "#059669" : "transparent",
                        color: form.status === "APPROVED" ? "#fff" : "#64748B",
                        "&:hover": {
                          borderColor: "#059669",
                          bgcolor: form.status === "APPROVED" ? "#047857" : "rgba(5, 150, 105, 0.08)",
                        },
                      }}
                    >
                      Approved
                    </Button>
                    <Button
                      variant={form.status === "REJECTED" ? "contained" : "outlined"}
                      onClick={() => setForm((prev) => ({ ...prev, status: "REJECTED" }))}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        flex: 1,
                        minWidth: { xs: 0, sm: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        px: { xs: 1, sm: 2 },
                        borderColor: form.status === "REJECTED" ? "#DC2626" : "#E5E7EB",
                        bgcolor: form.status === "REJECTED" ? "#DC2626" : "transparent",
                        color: form.status === "REJECTED" ? "#fff" : "#64748B",
                        "&:hover": {
                          borderColor: "#DC2626",
                          bgcolor: form.status === "REJECTED" ? "#B91C1C" : "rgba(220, 38, 38, 0.08)",
                        },
                      }}
                    >
                      Rejected
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>


            {isAssignedUser && (
              <Grid item size={{ xs: 12 }}>
                <TextField
                  label="Completion Remarks"
                  name="completion_remarks"
                  value={form.completion_remarks}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe what was completed…"
                  sx={FIELD_STYLE}
                />
              </Grid>
            )}

            {!isAssignedUser && (
              <Grid item size={{ xs: 12 }}>
                <TextField
                  label="Action Remarks"
                  name="action_remarks"
                  value={form.action_remarks}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Any action notes…"
                  sx={FIELD_STYLE}
                />
              </Grid>
            )}

            {isAssignedUser && (
              <Grid item size={{ xs: 12 }}>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddPhotoAlternate />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      borderColor: "#4318FF",
                      color: "#4318FF",
                      fontFamily: "DM Sans, sans-serif",
                      mb: imgPreviews.length ? 1.5 : 0,
                    }}
                  >
                    Upload Images
                  </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleAddImages}
                />
                {imgPreviews.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                    {imgPreviews.map((src, i) => (
                      <Box key={i} sx={{ position: "relative", width: 80, height: 80 }}>
                        <Box
                          component="img"
                          src={src}
                          alt={`preview-${i}`}
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #E8EDF5",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(i)}
                          sx={{
                            position: "absolute", top: -8, right: -8,
                            bgcolor: "error.main", color: "#fff",
                            width: 22, height: 22,
                            "&:hover": { bgcolor: "error.dark" },
                          }}
                        >
                          <Delete sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Grid>
            )}

          </Grid>

          {/* Form Actions */}
          <Stack direction="row" justifyContent="flex-end" gap={1.5} mt={4}>
            <Button
              variant="outlined"
              color="inherit"
              disabled={updateLoading}
              onClick={() => navigate(`${basePath}/dailyTask`)}
              sx={{ textTransform: "none", borderRadius: "10px", fontFamily: "DM Sans, sans-serif" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={updateLoading}
              startIcon={updateLoading ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 600,
                bgcolor: "#4318FF",
                "&:hover": { bgcolor: "#3010cc" },
              }}
            >
              {updateLoading ? "Updating…" : "Update Task"}
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}
